import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { NumberRequestsAPI } from "../api/number-requests";
import type {
  AvailableNumbers,
  AvailableNumberType,
  CreateNumberRequestData,
  NumberRequest,
  NumberRequestState,
  UpdateNumberRequestData,
} from "../types";

export const numberRequestKeys = {
  all: ["number_requests"] as const,
  list: (clientId: string) =>
    [...numberRequestKeys.all, "list", clientId] as const,
  detail: (clientId: string, requestId: string) =>
    [...numberRequestKeys.all, "detail", clientId, requestId] as const,
};

export function useNumberRequests(
  clientId: string,
  options?: { enabled?: boolean },
) {
  return useQuery<NumberRequest[]>({
    queryKey: numberRequestKeys.list(clientId),
    queryFn: () => NumberRequestsAPI.list(clientId),
    enabled: !!clientId && options?.enabled !== false,
    staleTime: 30 * 1000,
  });
}

export function useNumberRequest(clientId: string, requestId: string) {
  return useQuery<NumberRequest>({
    queryKey: numberRequestKeys.detail(clientId, requestId),
    queryFn: () => NumberRequestsAPI.get(clientId, requestId),
    enabled: !!clientId && !!requestId,
    staleTime: 30 * 1000,
  });
}

/**
 * The numbers offered by the creation dialog. Kept a minute: the carrier's
 * inventory moves, but not while someone is choosing.
 */
export function useAvailableNumbers(
  clientId: string,
  query: {
    type: AvailableNumberType;
    country?: string;
    prefix?: string;
    postcode?: string;
    address?: string;
  },
  options?: { enabled?: boolean },
) {
  return useQuery<AvailableNumbers>({
    queryKey: [...numberRequestKeys.all, "available", clientId, query],
    queryFn: () => NumberRequestsAPI.availableNumbers(clientId, query),
    enabled: !!clientId && options?.enabled !== false,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useCreateNumberRequest(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateNumberRequestData) =>
      NumberRequestsAPI.create(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: numberRequestKeys.list(clientId),
      });
    },
  });
}

/**
 * Every per-request mutation returns the updated row, so the detail cache is
 * seeded from the response and only the list is refetched. Without this the
 * detail page blanks out and re-renders on each upload, which reads as the
 * file having been lost.
 */
function useRequestMutation<TVariables>(
  clientId: string,
  mutationFn: (variables: TVariables) => Promise<NumberRequest>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (request) => {
      queryClient.setQueryData(
        numberRequestKeys.detail(clientId, request.id),
        request,
      );
      queryClient.invalidateQueries({
        queryKey: numberRequestKeys.list(clientId),
      });
    },
  });
}

export function useUpdateNumberRequest(clientId: string) {
  return useRequestMutation(
    clientId,
    ({
      requestId,
      data,
    }: {
      requestId: string;
      data: UpdateNumberRequestData;
    }) => NumberRequestsAPI.update(clientId, requestId, data),
  );
}

export function useTransitionNumberRequest(clientId: string) {
  return useRequestMutation(
    clientId,
    ({
      requestId,
      state,
      comment,
    }: {
      requestId: string;
      state: NumberRequestState;
      comment?: string;
    }) => NumberRequestsAPI.transition(clientId, requestId, state, comment),
  );
}

export function useUploadNumberRequestDocument(clientId: string) {
  return useRequestMutation(
    clientId,
    ({
      requestId,
      type,
      file,
    }: {
      requestId: string;
      type: string;
      file: File;
    }) => NumberRequestsAPI.uploadDocument(clientId, requestId, type, file),
  );
}

export function useRemoveNumberRequestDocument(clientId: string) {
  return useRequestMutation(
    clientId,
    ({ requestId, type }: { requestId: string; type: string }) =>
      NumberRequestsAPI.removeDocument(clientId, requestId, type),
  );
}

export function useDeleteNumberRequest(clientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: string) =>
      NumberRequestsAPI.remove(clientId, requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: numberRequestKeys.list(clientId),
      });
    },
  });
}
